import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';

// Public pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterSchool from './pages/RegisterSchool';
import RegisterMember from './pages/RegisterMember';
import QRVerifyPage from './pages/QRVerifyPage';
import TentangRSS from './pages/TentangRSS';
import Kepimpinan from './pages/Kepimpinan';
import Wilayah from './pages/Wilayah';
import Pencapaian from './pages/Pencapaian';
import Aktiviti from './pages/Aktiviti';
import DirektoriPerguruan from './pages/DirektoriPerguruan';
import DirektoriPesilat from './pages/DirektoriPesilat';
import Galeri from './pages/Galeri';
import Hubungi from './pages/Hubungi';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminSchools from './pages/admin/Schools';
import AdminSchoolDetail from './pages/admin/SchoolDetail';
import AdminSchoolForm from './pages/admin/SchoolForm';
import AdminMembers from './pages/admin/Members';
import AdminMemberForm from './pages/admin/MemberForm';
import AdminMemberProfile from './pages/admin/MemberProfile';
import AdminSettings from './pages/admin/Settings';

// School pages
import SchoolDashboard from './pages/school/Dashboard';
import SchoolProfile from './pages/school/Profile';
import SchoolMembers from './pages/school/Members';
import SchoolMemberForm from './pages/school/MemberForm';
import SchoolMemberProfile from './pages/school/MemberProfile';
import SchoolPendingMembers from './pages/school/PendingMembers';

import { AuthProvider, useAuth } from './context/AuthContext';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F7]">
        <div className="animate-pulse text-[#D71920] text-sm font-medium">Memuatkan...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/portal-ahli" replace />;

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* ── Public Routes ────────────────────────────── */}
          <Route path="/" element={<LandingPage />} />
          {/* New official routes */}
          <Route path="/tentang-rss" element={<TentangRSS />} />
          <Route path="/kepimpinan" element={<Kepimpinan />} />
          <Route path="/wilayah" element={<Wilayah />} />
          <Route path="/pencapaian" element={<Pencapaian />} />
          {/* Legacy redirects — keep old links working */}
          <Route path="/tentang-kami" element={<Navigate to="/tentang-rss" replace />} />
          <Route path="/ajk" element={<Navigate to="/kepimpinan" replace />} />
          <Route path="/aktiviti" element={<Aktiviti />} />
          <Route path="/direktori-perguruan" element={<DirektoriPerguruan />} />
          <Route path="/direktori-pesilat" element={<DirektoriPesilat />} />
          <Route path="/galeri" element={<Galeri />} />
          <Route path="/hubungi" element={<Hubungi />} />

          {/* Portal / Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/portal-ahli" element={<LoginPage />} />
          <Route path="/register-school" element={<RegisterSchool />} />
          <Route path="/register-member" element={<RegisterMember />} />
          <Route path="/verify" element={<QRVerifyPage />} />

          {/* ── Admin Protected Routes ───────────────────── */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/schools" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminSchools />
            </ProtectedRoute>
          } />
          <Route path="/admin/schools/new" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminSchoolForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/schools/:id" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminSchoolDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin/members" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminMembers />
            </ProtectedRoute>
          } />
          <Route path="/admin/members/new" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminMemberForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/members/:id" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminMemberProfile />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute allowedRoles={['RSS_ADMIN']}>
              <AdminSettings />
            </ProtectedRoute>
          } />

          {/* ── School Protected Routes ──────────────────── */}
          <Route path="/school/dashboard" element={
            <ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'SCHOOL_STAFF']}>
              <SchoolDashboard />
            </ProtectedRoute>
          } />
          <Route path="/school/profile" element={
            <ProtectedRoute allowedRoles={['SCHOOL_ADMIN']}>
              <SchoolProfile />
            </ProtectedRoute>
          } />
          <Route path="/school/members" element={
            <ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'SCHOOL_STAFF']}>
              <SchoolMembers />
            </ProtectedRoute>
          } />
          <Route path="/school/members/new" element={
            <ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'SCHOOL_STAFF']}>
              <SchoolMemberForm />
            </ProtectedRoute>
          } />
          {/* NOTE: /pending MUST be before /:id */}
          <Route path="/school/members/pending" element={
            <ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'SCHOOL_STAFF']}>
              <SchoolPendingMembers />
            </ProtectedRoute>
          } />
          <Route path="/school/members/:id/edit" element={
            <ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'SCHOOL_STAFF']}>
              <SchoolMemberForm />
            </ProtectedRoute>
          } />
          <Route path="/school/members/:id" element={
            <ProtectedRoute allowedRoles={['SCHOOL_ADMIN', 'SCHOOL_STAFF']}>
              <SchoolMemberProfile />
            </ProtectedRoute>
          } />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
